/**
 * About / Documentation Page
 * ==========================
 * User guide and documentation for Exodus Asset Management.
 */

import {
  Package, FolderTree, MapPin, Building2, Image, Barcode,
  Sparkles, Search, Moon, Monitor, Server, Bike, Ticket,
  ChevronRight
} from 'lucide-react'

interface FeatureSectionProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}

function FeatureSection({ icon, title, children }: FeatureSectionProps) {
  return (
    <div className="mb-8">
      <h2 className="flex items-center gap-3 text-xl font-semibold text-gray-900 dark:text-white mb-4">
        <span className="p-2 rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400">
          {icon}
        </span>
        {title}
      </h2>
      <div className="text-gray-600 dark:text-gray-300 space-y-3 ml-12">
        {children}
      </div>
    </div>
  )
}

function KeyboardShortcut({ keys, description }: { keys: string; description: string }) {
  return (
    <div className="flex items-center gap-3">
      <kbd className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
        {keys}
      </kbd>
      <span className="text-sm text-gray-600 dark:text-gray-400">{description}</span>
    </div>
  )
}

export default function About() {
  const currentYear = new Date().getFullYear()

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Exodus Documentation
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Complete user guide for Exodus Asset Management System
        </p>
      </div>

      {/* Quick Overview */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 mb-8 text-white">
        <h2 className="text-xl font-semibold mb-3">What is Exodus?</h2>
        <p className="text-primary-100 mb-4">
          Exodus is a comprehensive asset and inventory management system for tracking computer parts,
          IT infrastructure, motorcycles, and more. It features AI-powered product identification, barcode scanning,
          IT asset monitoring with live hardware detection, a ticketing system with email and webhook integration,
          and seamless Microsoft 365 authentication.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
          <div className="text-center">
            <div className="text-2xl font-bold">Inventory</div>
            <div className="text-xs text-primary-200">Parts & Products</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">Assets</div>
            <div className="text-xs text-primary-200">IT Infrastructure</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">Tickets</div>
            <div className="text-xs text-primary-200">Issue Tracking</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">AI</div>
            <div className="text-xs text-primary-200">Product ID</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">SSO</div>
            <div className="text-xs text-primary-200">Microsoft 365</div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Table of Contents
        </h2>
        <div className="grid md:grid-cols-2 gap-2">
          {[
            { icon: <Package size={16} />, label: 'Product Management' },
            { icon: <Server size={16} />, label: 'IT Asset Management' },
            { icon: <Ticket size={16} />, label: 'Ticketing System' },
            { icon: <Bike size={16} />, label: 'Motorcycles' },
            { icon: <FolderTree size={16} />, label: 'Categories' },
            { icon: <MapPin size={16} />, label: 'Locations' },
            { icon: <Building2 size={16} />, label: 'Manufacturers' },
            { icon: <Barcode size={16} />, label: 'Barcode Scanning' },
            { icon: <Sparkles size={16} />, label: 'AI Features' },
            { icon: <Image size={16} />, label: 'Image Management' },
            { icon: <Search size={16} />, label: 'Search' },
            { icon: <Monitor size={16} />, label: 'Desktop App' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <ChevronRight size={14} className="text-primary-500" />
              {item.icon}
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Features Documentation */}
      <div className="space-y-2">

        <FeatureSection icon={<Package size={20} />} title="Product Management">
          <p>
            Products are the core of Exodus. Each product has a unique ID (e.g., EXO-0001),
            and can store detailed information including SKU, barcode, description, quantity,
            pricing, and multiple images.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Adding a Product</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Click "Add Product" from the Dashboard or Products page</li>
            <li>Fill in the required fields: Name, Category, and Location</li>
            <li>Optionally add SKU, barcode, description, and images</li>
            <li>Use "Lookup" buttons to auto-fill details from SKU or barcode</li>
            <li>Click "Save Product" or "Save & Add Another" for batch entry</li>
          </ol>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Product Status</h3>
          <p className="text-sm">
            Track the lifecycle of products with status options:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
            <li><strong>In Stock</strong> — Available in inventory</li>
            <li><strong>In Use</strong> — Currently deployed/installed</li>
            <li><strong>On the Way</strong> — Ordered, awaiting delivery</li>
            <li><strong>Faulty</strong> — Defective or needs repair</li>
          </ul>
        </FeatureSection>

        <FeatureSection icon={<Server size={20} />} title="IT Asset Management">
          <p>
            Track IT infrastructure including servers, network devices, NAS units, cameras, and UPS systems.
            Assets can be auto-discovered via IP address using integrations with Proxmox, UniFi Network,
            UniFi Protect, and Synology NAS devices.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Supported Asset Types</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Servers</strong> — Proxmox VE hosts, Proxmox Backup Server, bare metal servers with iDRAC/IPMI</li>
            <li><strong>Network Devices</strong> — UniFi switches, access points, gateways, routers</li>
            <li><strong>Storage</strong> — Synology NAS with drive health, RAID status, and storage volumes</li>
            <li><strong>Cameras</strong> — UniFi Protect cameras, doorbell, and chime via Protect API</li>
            <li><strong>Power</strong> — UPS systems with battery and load info</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Add by IP</h3>
          <p className="text-sm">
            Enter any IP address and Exodus will query your infrastructure to auto-detect the device:
          </p>
          <ol className="list-decimal list-inside space-y-1 text-sm mt-2">
            <li>Click "Add by IP" on the Assets Dashboard or sub-page</li>
            <li>Enter the device IP address</li>
            <li>Review the auto-filled information (name, specs, network interfaces, drives)</li>
            <li>Save the asset</li>
          </ol>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Infrastructure Pages</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Proxmox Hosts</strong> — Servers with real-time CPU/RAM utilization gauges, storage, and VM counts</li>
            <li><strong>Synology NAS</strong> — NAS units with DSM version, update indicators, RAID status, storage volumes, and drive health</li>
            <li><strong>UniFi Network</strong> — Switches, access points, and gateways with firmware update indicators, port tables, and color-coded link speeds</li>
            <li><strong>Disk Health</strong> — Unified cross-asset drive overview with health status, temperature, power-on hours, and wear level</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Auto-Resync</h3>
          <p className="text-sm">
            Infrastructure list pages (Proxmox, Synology, UniFi) automatically resync all assets when opened,
            pulling the latest hardware data with per-row loading indicators. Assets that become unreachable
            are automatically marked offline.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Hardware Detection</h3>
          <p className="text-sm">
            For servers and NAS, Exodus uses SSH to detect hardware details:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
            <li>Model, serial number, firmware/BIOS version</li>
            <li>Network interfaces with MAC, speed (color-coded), and link status</li>
            <li>Storage drives with type (SSD/HDD/NVMe), serial, health, temperature, and SMART data</li>
            <li>Power supply count and redundancy status</li>
            <li>CPU and RAM utilization (Proxmox, iDRAC Redfish)</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Virtual Console</h3>
          <p className="text-sm">
            Launch remote management consoles directly from asset views — supports iDRAC 8/9,
            Supermicro HTML5 IPMI, and generic IPMI interfaces for one-click server access.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Ticket size={20} />} title="Ticketing System">
          <p>
            Full-featured ticketing system for tracking issues, maintenance tasks, and alerts across your
            infrastructure. Tickets can be created manually, from emails, or automatically via webhook alerts
            from monitoring tools like Grafana.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Ticket Management</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Each ticket gets a unique ID (e.g., TIK-0001)</li>
            <li>Status tracking: Open, In Progress, Resolved, Closed</li>
            <li>Priority levels: Low, Medium, High, Critical</li>
            <li>Categories: Hardware, Software, Network, Security, Access, Other</li>
            <li>Link tickets to IT assets for context</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Comments & Timeline</h3>
          <p className="text-sm">
            Every ticket has an activity timeline showing comments, status changes, and system events.
            Add notes, track progress, and see the full history of each issue.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">File Attachments</h3>
          <p className="text-sm">
            Upload files to tickets with drag-and-drop support. Supports images, PDFs, Office documents,
            spreadsheets, and archives up to 50MB per file.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Email-to-Ticket</h3>
          <p className="text-sm">
            Emails sent to the shared mailbox are automatically converted into tickets. Attachments are
            preserved, and the sender receives a confirmation email with the ticket ID.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Webhook Integration (Grafana)</h3>
          <p className="text-sm">
            Monitoring alerts from Grafana automatically create tickets via webhook. Features include:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
            <li>Duplicate detection — same alert adds a comment instead of a new ticket</li>
            <li>Auto-resolve — tickets close when the alert clears</li>
            <li>Auto-reopen — resolved tickets reopen if the alert fires again</li>
            <li>Asset auto-linking — matches alerts to assets by IP or hostname</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Email Notifications</h3>
          <p className="text-sm">
            When a ticket status changes, the original sender receives an email notification with the
            old and new status. Resolution notes can be added when marking tickets as Resolved or Closed,
            and are included in the notification email.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Bike size={20} />} title="Motorcycles">
          <p>
            Track your motorcycle collection with dedicated pages for each bike, including service history,
            ownership records, and parts inventory. The Motorcycles module operates alongside the main
            Inventory system, sharing locations and manufacturers.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Sub-Pages per Motorcycle</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Information</strong> — Basic details, specs, VIN, status</li>
            <li><strong>History</strong> — Ownership changes, accidents, modifications, milestones</li>
            <li><strong>Service</strong> — Maintenance and repair records with dates, mileage, costs</li>
            <li><strong>Restoration/Build</strong> — Documentation for restoration projects</li>
            <li><strong>Parts on Stock</strong> — Inventory items associated with this bike</li>
            <li><strong>Parts Search</strong> — Search vendors for compatible parts</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Motorcycle Status</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Running</strong> — Operational and rideable</li>
            <li><strong>Restoration</strong> — Currently being restored</li>
            <li><strong>Project</strong> — Future build or modification project</li>
            <li><strong>Storage</strong> — In storage, not currently in use</li>
            <li><strong>Sold</strong> — No longer owned</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Vendor Management</h3>
          <p className="text-sm">
            Track parts suppliers and service providers with name, website, country, and notes.
            Link vendors to service records to know where work was performed.
          </p>
        </FeatureSection>

        <FeatureSection icon={<FolderTree size={20} />} title="Categories">
          <p>
            Organize products with hierarchical categories. Categories can be nested
            to any depth (e.g., Electronics → Computers → Laptops → Gaming Laptops).
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Managing Categories</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Create top-level categories for broad groupings</li>
            <li>Add subcategories by clicking "Add Subcategory" on any category</li>
            <li>Click a category name to view all products within it (includes subcategories)</li>
            <li>Categories with products cannot be deleted</li>
          </ul>
        </FeatureSection>

        <FeatureSection icon={<MapPin size={20} />} title="Locations">
          <p>
            Track where products are physically stored. Locations help you find items
            quickly and manage inventory across multiple storage areas.
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
            <li>Create locations for shelves, rooms, buildings, or any storage area</li>
            <li>Assign a default location in the product form</li>
            <li>Locations with products cannot be deleted</li>
          </ul>
        </FeatureSection>

        <FeatureSection icon={<Building2 size={20} />} title="Manufacturers">
          <p>
            Keep track of product brands and manufacturers. Manufacturer logos are
            automatically fetched when you create a new manufacturer.
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
            <li>Manufacturers are auto-created from barcode lookups</li>
            <li>Click a manufacturer to see all their products</li>
            <li>Logos are fetched automatically via Google Favicon API</li>
          </ul>
        </FeatureSection>

        <FeatureSection icon={<Barcode size={20} />} title="Barcode Scanning">
          <p>
            Quickly add or find products by scanning barcodes. Exodus supports EAN-13,
            EAN-8, UPC-A, UPC-E, CODE-128, and CODE-39 formats.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Scanning Methods</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Camera Scanner</strong> — Click the camera icon next to the barcode field</li>
            <li><strong>Manual Entry</strong> — Type the barcode number directly</li>
            <li><strong>AI Fallback</strong> — If auto-scan fails, use "AI Read" to analyze the image</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Dashboard Quick Scan</h3>
          <p className="text-sm">
            Use the camera icon next to the search bar on the Dashboard to quickly find
            products by barcode. If not found, you'll be prompted to add it as a new product.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Sparkles size={20} />} title="AI Features">
          <p>
            Exodus uses Claude AI to help identify products and fill in details automatically.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">SKU Lookup</h3>
          <p className="text-sm">
            Enter a SKU or part number and click "Lookup" to search the web for product
            information. Claude AI extracts the name, description, manufacturer, and finds
            product images.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">AI Product Identification</h3>
          <p className="text-sm">
            Take a photo of any product and let AI identify it:
          </p>
          <ol className="list-decimal list-inside space-y-1 text-sm mt-2">
            <li>In the product form, find "AI Product Identification" in the sidebar</li>
            <li>Click to open the camera and take a photo</li>
            <li>Claude Vision analyzes the image and searches for matches</li>
            <li>Review the results and select which fields to apply</li>
          </ol>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Barcode Lookup</h3>
          <p className="text-sm">
            Enter a barcode and click "Lookup" to query product databases (EAN-Search, UPCitemdb)
            for product information. Works best with retail products.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Image size={20} />} title="Image Management">
          <p>
            Products can have multiple images. The primary image is shown as the thumbnail
            in lists and search results.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Adding Images</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>File Upload</strong> — Drag and drop or browse for images (max 20MB)</li>
            <li><strong>URL</strong> — Paste an image URL to download and save</li>
            <li><strong>Google Image Search</strong> — Search for product images by name</li>
            <li><strong>AI Identification</strong> — Images found during AI lookup can be added</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Image Gallery</h3>
          <p className="text-sm">
            View all product images in the Images page. Filter by category and click
            any image to navigate to its product.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Search size={20} />} title="Search">
          <p>
            Find products quickly using the search bar on the Dashboard or Products page.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Searchable Fields</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Product ID (EXO-0001)</li>
            <li>SKU / Part Number</li>
            <li>Product Name</li>
            <li>Barcode</li>
            <li>Description / Details</li>
            <li>Service Tag</li>
            <li>Manufacturer Name</li>
          </ul>
          <p className="text-sm mt-2">
            Search is case-insensitive and matches partial text. Start typing to see
            autocomplete suggestions.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Moon size={20} />} title="Dark Mode">
          <p>
            Toggle between light and dark themes using the sun/moon icon in the header.
            Your preference is saved and persists across sessions.
          </p>
        </FeatureSection>

        <FeatureSection icon={<Monitor size={20} />} title="Desktop Application">
          <p>
            Exodus is available as a native Windows application for a dedicated desktop experience.
          </p>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Features</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Native Windows window with taskbar integration</li>
            <li>Remembers window size and position</li>
            <li>Auto-update notifications when new versions are available</li>
            <li>Full Microsoft 365 SSO support</li>
          </ul>
          <h3 className="font-medium text-gray-900 dark:text-white mt-4 mb-2">Keyboard Shortcuts (Desktop)</h3>
          <div className="space-y-2 mt-2">
            <KeyboardShortcut keys="Ctrl+R" description="Reload the application" />
            <KeyboardShortcut keys="Ctrl++" description="Zoom in" />
            <KeyboardShortcut keys="Ctrl+-" description="Zoom out" />
            <KeyboardShortcut keys="Ctrl+0" description="Reset zoom" />
            <KeyboardShortcut keys="F11" description="Toggle fullscreen" />
          </div>
        </FeatureSection>

      </div>

      {/* Credits */}
      <div className="mt-12 pt-8 border-t dark:border-gray-700">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            About Exodus
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Exodus Asset Management System
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-2">
            Created by Ásgeir Helgi Ásgeirsson Holm
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 flex items-center justify-center gap-2">
            <Sparkles size={14} className="text-primary-500" />
            Written by Claude "The Machine" Code
            <Sparkles size={14} className="text-primary-500" />
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-600 mt-4">
            © {currentYear} Ásgeir Helgi Ásgeirsson Holm. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
